import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

interface WelcomeMessageEditorProps {
  morningText: string;
  afternoonText: string;
  eveningText: string;
  secondaryText: string;
  restaurantName: string;
  morningHour: number;
  afternoonHour: number;
  eveningHour: number;
  textColor: string;
  gradientStartColor: string;
  gradientEndColor: string;
  borderColor: string;
  onMorningTextChange: (text: string) => void;
  onAfternoonTextChange: (text: string) => void;
  onEveningTextChange: (text: string) => void;
  onSecondaryTextChange: (text: string) => void;
  onRestaurantNameChange: (name: string) => void;
  onMorningHourChange: (hour: number) => void;
  onAfternoonHourChange: (hour: number) => void;
  onEveningHourChange: (hour: number) => void;
  onTextColorChange: (color: string) => void;
  onGradientStartColorChange: (color: string) => void;
  onGradientEndColorChange: (color: string) => void;
  onBorderColorChange: (color: string) => void;
  primaryColor: string;
  secondaryColor: string;
}

/**
 * WelcomeMessageEditor - UI for customising welcome message settings
 */
const WelcomeMessageEditor: React.FC<WelcomeMessageEditorProps> = ({
  morningText,
  afternoonText,
  eveningText,
  secondaryText,
  restaurantName,
  morningHour,
  afternoonHour,
  eveningHour,
  textColor,
  gradientStartColor,
  gradientEndColor,
  borderColor,
  onMorningTextChange,
  onAfternoonTextChange,
  onEveningTextChange,
  onSecondaryTextChange,
  onRestaurantNameChange,
  onMorningHourChange,
  onAfternoonHourChange,
  onEveningHourChange,
  onTextColorChange,
  onGradientStartColorChange,
  onGradientEndColorChange,
  onBorderColorChange,
  primaryColor,
  secondaryColor
}) => {
  const colorPresets = [
    { name: 'Primary', color: primaryColor },
    { name: 'Secondary', color: secondaryColor },
    { name: 'White', color: '#ffffff' },
    { name: 'Black', color: '#2D2D2D' },
    { name: 'Green', color: '#3EA637' },
    { name: 'Blue', color: '#3f92ff' },
    { name: 'Red', color: '#e74c3c' },
    { name: 'Transparent', color: 'transparent' }
  ];

  const resetToDefaults = () => {
    onMorningTextChange('Good morning!');
    onAfternoonTextChange('Good afternoon!');
    onEveningTextChange('Good evening!');
    onSecondaryTextChange('Welcome to {restaurant name}!');
    onRestaurantNameChange('our restaurant');
    onMorningHourChange(12);
    onAfternoonHourChange(17);
    onEveningHourChange(24);
    onTextColorChange('#ffffff');
    onGradientStartColorChange(primaryColor);
    onGradientEndColorChange(secondaryColor);
    onBorderColorChange('transparent');
  };

  const copyBrandColors = () => {
    onGradientStartColorChange(primaryColor);
    onGradientEndColorChange(secondaryColor);
  };

  const removeGradient = () => {
    onGradientEndColorChange(primaryColor);
  };

  const renderColorPicker = (
    value: string,
    onChange: (color: string) => void,
    label: string
  ) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start gap-2">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: value }}
              />
              {value}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-3">
              <Input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-10"
              />
              <Input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#ffffff"
                className="font-mono text-sm"
              />
              <div className="grid grid-cols-2 gap-1">
                {colorPresets.map((preset) => (
                  <Button
                    key={preset.name}
                    variant="outline"
                    size="sm"
                    onClick={() => onChange(preset.color)}
                    className="justify-start gap-2 h-auto p-2"
                  >
                    <div 
                      className="w-3 h-3 rounded border border-gray-300"
                      style={{ backgroundColor: preset.color }}
                    />
                    <span className="text-xs">{preset.name}</span>
                  </Button>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  const renderTimeInput = (
    value: number,
    onChange: (hour: number) => void,
    label: string,
    description: string
  ) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <Input
        type="number"
        min="0"
        max="24"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        className="w-full"
      />
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  );

  // Determine current time period for preview
  const currentHour = new Date().getHours();
  let currentGreeting = eveningText;
  if (currentHour < morningHour) {
    currentGreeting = morningText;
  } else if (currentHour < afternoonHour) {
    currentGreeting = afternoonText;
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Welcome Message Customisation</CardTitle>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={copyBrandColors}
            className="text-xs"
          >
            Use Brand Colours
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={removeGradient}
            className="text-xs"
          >
            Remove Gradient
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={resetToDefaults}
            className="text-xs"
          >
            Reset to Defaults
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Message Text Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-sm">Message Text</h4>
              <Separator className="flex-1" />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Morning Greeting</Label>
              <Input
                value={morningText}
                onChange={(e) => onMorningTextChange(e.target.value)}
                placeholder="Good morning!"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Afternoon Greeting</Label>
              <Input
                value={afternoonText}
                onChange={(e) => onAfternoonTextChange(e.target.value)}
                placeholder="Good afternoon!"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Evening Greeting</Label>
              <Input
                value={eveningText}
                onChange={(e) => onEveningTextChange(e.target.value)}
                placeholder="Good evening!"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Secondary Message</Label>
              <Textarea
                value={secondaryText}
                onChange={(e) => onSecondaryTextChange(e.target.value)}
                placeholder="Welcome to {restaurant name}!"
                rows={2}
              />
              <p className="text-xs text-gray-500">
                Use {'{restaurant name}'} to include the restaurant name dynamically
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Restaurant Name</Label>
              <Input
                value={restaurantName}
                onChange={(e) => onRestaurantNameChange(e.target.value)}
                placeholder="our restaurant"
              />
              <p className="text-xs text-gray-500">
                This replaces {'{restaurant name}'} in your messages. Auto-filled from your restaurant settings.
              </p>
            </div>
          </div>

          {/* Time Periods Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-sm">Time Periods</h4>
              <Separator className="flex-1" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {renderTimeInput(
                morningHour,
                onMorningHourChange,
                'Morning Until (Hour)',
                `Morning greeting shows from 00:00 to ${morningHour.toString().padStart(2, '0')}:59`
              )}
              
              {renderTimeInput(
                afternoonHour,
                onAfternoonHourChange,
                'Afternoon Until (Hour)',
                `Afternoon greeting shows from ${morningHour.toString().padStart(2, '0')}:00 to ${afternoonHour.toString().padStart(2, '0')}:59`
              )}
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Evening From (Hour)</Label>
                <Input
                  value={afternoonHour}
                  disabled
                  className="w-full bg-gray-50"
                />
                <p className="text-xs text-gray-500">
                  Evening greeting shows from {afternoonHour.toString().padStart(2, '0')}:00 onwards
                </p>
              </div>
            </div>
          </div>

          {/* Colors Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-sm">Colours</h4>
              <Separator className="flex-1" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderColorPicker(gradientStartColor, onGradientStartColorChange, 'Gradient Start')}
              {renderColorPicker(gradientEndColor, onGradientEndColorChange, 'Gradient End')}
              {renderColorPicker(textColor, onTextColorChange, 'Text Color')}
              {renderColorPicker(borderColor, onBorderColorChange, 'Border Color')}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="mt-6 p-4 bg-gray-100 rounded-md">
          <Label className="text-sm font-medium mb-2 block">Live Preview (Current Time: {currentHour}:00)</Label>
          <div 
            className="inline-block p-4 rounded-xl shadow-lg max-w-xs"
            style={{
              background: `linear-gradient(135deg, ${gradientStartColor}, ${gradientEndColor})`,
              border: borderColor === 'transparent' ? 'none' : `2px solid ${borderColor}`,
              fontFamily: 'inherit'
            }}
          >
            <div 
              className="font-bold text-base"
              style={{ color: textColor }}
            >
              {currentGreeting}
            </div>
            <div 
              className="text-sm mt-1 opacity-90"
              style={{ color: textColor }}
            >
              {secondaryText.replace('{restaurant name}', restaurantName)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WelcomeMessageEditor;